import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import {
  HomeIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  CreditCardIcon,
  PuzzlePieceIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  NewspaperIcon,
  SparklesIcon,
  UserGroupIcon,
  BookOpenIcon,
  ClockIcon,
  ShieldExclamationIcon,
  NoSymbolIcon,
  ClipboardDocumentListIcon,
  RocketLaunchIcon,
  SignalIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const mainNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Accounts', href: '/accounts', icon: BuildingOfficeIcon },
  { name: 'Wizard Sessions', href: '/wizard-sessions', icon: DocumentTextIcon },
  { name: 'Discovery', href: '/discovery', icon: EyeIcon },
  { name: 'Websites', href: '/websites', icon: GlobeAltIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Subscriptions', href: '/subscriptions', icon: CreditCardIcon },
  { name: 'Bundles', href: '/bundles', icon: SparklesIcon },
  { name: 'Affiliates', href: '/affiliates', icon: UserGroupIcon },
  { name: 'Commissions', href: '/commissions', icon: CurrencyDollarIcon, showBadge: true },
  { name: 'AI Audit', href: '/ai-customizations', icon: SparklesIcon },
];

const configNavigation = [
  { name: 'Integrations', href: '/integrations', icon: PuzzlePieceIcon },
  { name: 'Blog', href: '/blog', icon: NewspaperIcon },
  { name: 'Guides', href: '/knowledge-base', icon: BookOpenIcon },
  { name: 'Scheduled Jobs', href: '/jobs', icon: ClockIcon },
  { name: 'WhatsApp Broadcast', href: '/whatsapp-broadcast', icon: SignalIcon },
  { name: 'Release Notes', href: '/release-notes', icon: RocketLaunchIcon },
];

const securityNavigation = [
  { name: 'Risk Dashboard', href: '/risk', icon: ShieldExclamationIcon },
  { name: 'Risk Users', href: '/risk/users', icon: UserGroupIcon },
  { name: 'Blocked Entities', href: '/risk/blocked', icon: NoSymbolIcon },
  { name: 'Audit Trail', href: '/risk/audit', icon: ClipboardDocumentListIcon },
];

const Sidebar = ({ isOpen, setIsOpen }) => {
  const [pendingCommissionsCount, setPendingCommissionsCount] = useState(0);

  useEffect(() => {
    fetchPendingCommissionsCount();
    // Refresh counts every 30 seconds
    const interval = setInterval(() => {
      fetchPendingCommissionsCount();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingCommissionsCount = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/commissions?status=pending&limit=1`);
      setPendingCommissionsCount(response.data.data.total || 0);
    } catch (error) {
      console.error('Error fetching pending commissions count:', error);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:static lg:inset-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <a 
            href="/dashboard" 
            className="flex flex-col items-center justify-center py-4 px-4 border-b border-gray-200 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="46" viewBox="0 0 139 64" fill="none" className="mb-1">
              <path d="M46.488 39.1321C45.9083 38.4666 45.4798 37.6093 45.2026 36.56C44.9505 35.5107 44.8245 34.1286 44.8245 32.4139C44.8245 30.6992 45.1144 29.0613 45.6941 27.5001C46.2738 25.9389 47.0929 24.5953 48.1515 23.4692C50.3191 21.1147 53.1924 19.9374 56.7714 19.9374C58.0318 19.9374 59.1226 20.1465 60.0438 20.5649C60.0796 20.5812 60.119 20.5879 60.1581 20.584L66.2689 19.9671C66.4159 19.9523 66.5357 20.0852 66.5081 20.2326L63.7656 34.9092C63.69 35.2163 63.6522 35.6514 63.6522 36.2145C63.6522 36.7775 63.816 37.2382 64.1437 37.5965C64.4334 37.8906 64.774 38.0813 65.1656 38.1684C65.301 38.1986 65.3934 38.336 65.343 38.4672C65.0548 39.2177 64.4912 39.8232 63.6522 40.2837C62.7448 40.7956 61.7745 41.0515 60.7411 41.0515C59.7077 41.0515 58.8382 40.8468 58.1324 40.4373C57.6124 40.1439 57.2322 39.7684 56.9918 39.3106C56.9031 39.1418 56.647 39.1067 56.5244 39.2521C56.1306 39.7189 55.5955 40.114 54.9189 40.4373C54.0619 40.8468 53.0538 41.0515 51.8944 41.0515C50.7602 41.0515 49.7268 40.898 48.7942 40.5909C47.8617 40.2837 47.0929 39.7975 46.488 39.1321ZM54.4652 23.546C54.188 24.0067 53.9233 24.6337 53.6713 25.4271C53.4192 26.1948 53.079 27.628 52.6505 29.7267C52.2472 31.7997 52.0456 33.5784 52.0456 35.0628C52.0456 36.5472 52.159 37.5069 52.3858 37.942C52.6127 38.3771 52.9277 38.5946 53.331 38.5946C54.1375 38.5946 54.8307 38.2107 55.4104 37.4429C56.0153 36.6495 56.4311 35.5618 56.658 34.1798L58.6392 23.0585C58.6531 22.98 58.6254 22.8994 58.5639 22.8497C58.0612 22.4437 57.5142 22.2407 56.9226 22.2407C56.3177 22.2407 55.8262 22.3431 55.4482 22.5479C55.0953 22.7526 54.7677 23.0853 54.4652 23.546Z" fill="#4A4A4A"/>
              <path d="M86.2201 37.6733C85.1615 38.7482 83.9013 39.5799 82.4394 40.1686C81.0028 40.7572 79.5661 41.0515 78.1295 41.0515C76.9194 41.0515 76.0401 40.9607 75.4917 40.7792C75.3515 40.7328 75.1919 40.8149 75.1655 40.9622L74.0763 47.0271C74.0586 47.1258 73.9786 47.2006 73.8803 47.2104L66.6672 47.932C66.5198 47.9468 66.4 47.8132 66.4283 47.6656L71.5577 20.8685C71.5762 20.7715 71.6551 20.6982 71.7519 20.6879L77.456 20.0838C77.6033 20.0682 77.7238 20.2011 77.6964 20.3489L77.493 21.4451C77.4472 21.6923 77.8373 21.8587 78.0049 21.6735C79.029 20.5417 80.4064 19.9758 82.137 19.9758C84.607 19.9758 86.4973 20.7819 87.808 22.3943C88.9674 23.8531 89.5471 25.6958 89.5471 27.9224C89.5471 30.1489 89.2572 32.0556 88.6775 33.6424C88.0978 35.2291 87.2787 36.5728 86.2201 37.6733ZM75.6946 38.0072C75.6806 38.0857 75.7083 38.1663 75.7699 38.2159C76.274 38.6219 76.8337 38.8249 77.449 38.8249C78.1043 38.8249 78.621 38.697 78.999 38.4411C79.3771 38.1851 79.7047 37.8012 79.982 37.2894C80.7381 35.9074 81.4186 33.1177 82.0235 28.9205C82.2 27.692 82.2882 26.4764 82.2882 25.2735C82.2882 24.0706 82.1496 23.2773 81.8723 22.8934C81.6203 22.5095 81.2422 22.3175 80.7381 22.3175C79.2007 22.3175 78.1799 23.8403 77.6758 26.8859L75.6946 38.0072Z" fill="#4A4A4A"/>
              <path d="M109.443 37.6733C108.384 38.7482 107.124 39.5799 105.662 40.1686C104.226 40.7572 102.789 41.0515 101.352 41.0515C100.142 41.0515 99.2631 40.9607 98.7147 40.7792C98.5745 40.7328 98.4149 40.8149 98.3885 40.9622L97.2993 47.0271C97.2816 47.1258 97.2016 47.2006 97.1033 47.2104L89.8902 47.932C89.7428 47.9468 89.623 47.8132 89.6513 47.6656L94.7807 20.8685C94.7992 20.7715 94.8781 20.6982 94.9749 20.6879L100.679 20.0838C100.826 20.0682 100.947 20.2011 100.919 20.3489L100.716 21.4451C100.67 21.6923 101.06 21.8587 101.228 21.6735C102.252 20.5417 103.629 19.9758 105.36 19.9758C107.83 19.9758 109.72 20.7819 111.031 22.3943C112.19 23.8531 112.77 25.6958 112.77 27.9224C112.77 30.1489 112.48 32.0556 111.9 33.6424C111.321 35.2291 110.502 36.5728 109.443 37.6733ZM98.9176 38.0072C98.9036 38.0857 98.9313 38.1663 98.9929 38.2159C99.497 38.6219 100.057 38.8249 100.672 38.8249C101.327 38.8249 101.844 38.697 102.222 38.4411C102.6 38.1851 102.928 37.8012 103.205 37.2894C103.961 35.9074 104.642 33.1177 105.247 28.9205C105.423 27.692 105.511 26.4764 105.511 25.2735C105.511 24.0706 105.373 23.2773 105.095 22.8934C104.843 22.5095 104.465 22.3175 103.961 22.3175C102.424 22.3175 101.403 23.8403 100.899 26.8859L98.9176 38.0072Z" fill="#4A4A4A"/>
              <path d="M126.912 36.7085C126.958 36.4882 126.678 36.3306 126.505 36.4721C125.245 37.5032 123.581 38.0188 121.513 38.0188C118.514 38.0188 116.699 36.9055 116.069 34.6789C115.867 34.0135 115.767 33.3097 115.767 32.5675C115.767 31.8253 115.842 31.0703 115.993 30.3025L117.74 20.8707C117.758 20.7726 117.838 20.6984 117.935 20.6886L125.151 19.9668C125.298 19.9521 125.418 20.0848 125.39 20.2321L123.366 31.1087C123.315 31.4414 123.252 31.7869 123.177 32.1452C123.101 32.5035 123.063 32.8746 123.063 33.2585C123.063 33.6168 123.126 33.9239 123.252 34.1798C123.378 34.4102 123.529 34.6021 123.706 34.7557C124.008 34.9604 124.424 35.0628 124.954 35.0628C125.508 35.0628 126.012 34.7301 126.466 34.0647C126.945 33.3993 127.298 32.4907 127.524 31.339L129.572 20.8682C129.591 20.7713 129.67 20.6984 129.767 20.6884L136.756 19.9678C136.904 19.9526 137.024 20.0858 136.996 20.2335L134.216 35.0244C133.284 39.9382 131.86 43.3677 129.944 45.3127C128.961 46.3109 127.827 47.0019 126.541 47.3858C125.281 47.7953 123.832 48 122.194 48C119.547 48 117.468 47.5777 115.956 46.7332C114.443 45.8886 113.687 44.7625 113.687 43.3549C113.687 42.3056 114.078 41.4738 114.859 40.8596C115.64 40.2709 116.636 39.9766 117.846 39.9766C118.904 39.9766 119.837 40.207 120.644 40.6676C121.051 40.9069 121.367 41.174 121.592 41.4688C121.659 41.557 121.64 41.681 121.562 41.7599C121.076 42.2544 120.833 42.8884 120.833 43.662C120.833 44.7881 121.337 45.3511 122.345 45.3511C124.008 45.3511 125.319 43.4061 126.277 39.516C126.496 38.6042 126.708 37.6684 126.912 36.7085Z" fill="#4A4A4A"/>
              <path d="M19.8843 38.4854C19.7569 38.4555 19.6481 38.3831 19.5706 38.285C19.4163 38.0896 19.4986 37.8153 19.6011 37.5875L24.7388 26.1739C24.8407 25.9475 24.9167 25.6905 24.8197 25.4619C24.807 25.4319 24.7928 25.4033 24.7773 25.376C24.6388 25.1319 24.3975 24.9977 24.1681 24.9512C23.8223 24.8809 23.5788 25.0592 23.1127 25.5103L6.64131 44.0193C6.39772 44.3553 6.34121 44.8024 6.52371 45.1665C6.63698 45.3926 6.81577 45.5958 7.02506 45.7342C7.21322 45.8586 7.52624 46.0011 8.03991 46.1013C8.12791 46.1185 8.21788 46.1256 8.30748 46.1257L50.7079 46.206C51.3338 46.2068 51.6073 45.7494 51.6518 45.3582C51.6957 44.9681 51.5365 44.502 50.9578 44.3652L19.8843 38.4854Z" fill="#4A4A4A"/>
              <path d="M32.7744 22.7206C32.9018 22.7505 33.0106 22.8229 33.088 22.921C33.2424 23.1164 33.1601 23.3907 33.0576 23.6185L27.9199 35.0321C27.818 35.2585 27.7419 35.5155 27.839 35.7441C27.8517 35.7741 27.8659 35.8027 27.8814 35.83C28.0199 36.0741 28.2611 36.2083 28.4906 36.2548C28.8364 36.3251 29.0799 36.1468 29.546 35.6957L46.0174 17.1867C46.261 16.8507 46.3175 16.4036 46.135 16.0395C46.0217 15.8134 45.8429 15.6102 45.6336 15.4718C45.4455 15.3474 45.1324 15.2049 44.6188 15.1047C44.5308 15.0875 44.4408 15.0804 44.3512 15.0803L1.95082 15C1.32488 14.9992 1.05136 15.4566 1.00684 15.8478C0.963024 16.2379 1.12219 16.7041 1.70091 16.8409L32.7744 22.7206Z" fill="#FF0083"/>
            </svg>
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Admin</span>
          </a>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {/* Main Navigation */}
            <div className="space-y-1 mb-6">
              {mainNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setIsOpen(false)}
                >
                  <div className="flex items-center">
                    <item.icon
                      className="mr-3 h-5 w-5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {item.name}
                  </div>
                  {item.showBadge && (
                    <>
                      {item.name === 'Commissions' && pendingCommissionsCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-yellow-500 rounded-full">
                          {pendingCommissionsCount}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>

            {/* Configuration Section */}
            <div className="pt-4 border-t border-gray-200">
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Configuration
                </span>
              </div>
              <div className="space-y-1">
                {configNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center">
                      <item.icon
                        className="mr-3 h-5 w-5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      {item.name}
                    </div>
                  </NavLink>
                ))}
              </div>
            </div>

            {/* Security Section */}
            <div className="pt-4 border-t border-gray-200">
              <div className="px-3 mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Security
                </span>
              </div>
              <div className="space-y-1">
                {securityNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center">
                      <item.icon
                        className="mr-3 h-5 w-5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      {item.name}
                    </div>
                  </NavLink>
                ))}
              </div>
            </div>
          </nav>

          {/* Footer */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              Zappy Backoffice v1.0
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

